import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import IosShareIcon from '@mui/icons-material/IosShare';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

import {
  copyMarkdownToClipboard,
  downloadMarkdown,
  exportEditorToMarkdown,
} from '../utils/exportToMarkdown';

interface Props {
  /** Used as the download filename base (slugified inside the helper). */
  documentTitle?: string;
}

/**
 * Gap 2 — thin canvas toolbar with export actions. Lives inside the
 * <LexicalComposer> so it can read the editor's current state when the user
 * picks an export action. v3.0 ceiling: "Copy as Markdown" + "Download .md"
 * (spec 11 §1.3). Designed to stay compact (one button + menu) so it doesn't
 * crowd the canvas; v3.0.1 can grow it for formatting affordances when
 * BlockMarkupPlugin / mode toggles arrive.
 */
export const CanvasToolbar: React.FC<Props> = ({ documentTitle }) => {
  const [editor] = useLexicalComposerContext();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);

  const close = () => setAnchor(null);
  const open = Boolean(anchor);

  const handleCopy = async () => {
    close();
    const md = exportEditorToMarkdown(editor);
    if (!md.trim()) {
      setToast({ kind: 'error', message: 'Nothing to copy yet — start writing first.' });
      return;
    }
    try {
      await copyMarkdownToClipboard(md);
      setToast({ kind: 'success', message: 'Copied as Markdown.' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Copy failed.';
      setToast({ kind: 'error', message: msg });
    }
  };

  const handleDownload = () => {
    close();
    const md = exportEditorToMarkdown(editor);
    if (!md.trim()) {
      setToast({ kind: 'error', message: 'Nothing to download yet — start writing first.' });
      return;
    }
    downloadMarkdown(md, documentTitle ?? 'document');
    setToast({ kind: 'success', message: 'Downloaded as Markdown.' });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        px: 1,
        py: 0.5,
        borderBottom: theme => `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.default',
      }}
      aria-label="Canvas toolbar"
    >
      <Stack direction="row" spacing={0.5}>
        <Tooltip title="Export this document">
          <Button
            size="small"
            variant="text"
            color="inherit"
            startIcon={<IosShareIcon fontSize="small" />}
            onClick={e => setAnchor(e.currentTarget)}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={open ? 'canvas-toolbar-export-menu' : undefined}
          >
            Export
          </Button>
        </Tooltip>
      </Stack>
      <Menu
        id="canvas-toolbar-export-menu"
        anchorEl={anchor}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleCopy}>
          <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} />
          Copy as Markdown
        </MenuItem>
        <MenuItem onClick={handleDownload}>
          <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
          Download .md
        </MenuItem>
      </Menu>
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert
            severity={toast.kind}
            onClose={() => setToast(null)}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};
